import { Box, Button, Group, Paper, Stack, Text } from '@mantine/core';
import Badge from 'components/Badge';
import Card from 'components/Card';
import { labelMapping } from 'constant/course';
import React from 'react';
import { HiExternalLink, HiOutlineBookmark } from 'react-icons/hi';
import { BasicInformation } from 'types/lcms/courses';
import MaterialLink from './MaterialLink';
import { courseService } from 'services/lcms/course.service';
import { useQuery } from '@tanstack/react-query';

interface BasicInformationSectionProps {
  basicInformation?: BasicInformation & { creatorCode?: string };
  learningObjectives?: string;
  bcldModuleCode?: string;
  beelajarID?: string;
}

const BasicInformationSection: React.FC<BasicInformationSectionProps> = ({
  basicInformation,
  learningObjectives,
  beelajarID,
  bcldModuleCode
}) => {
  const ignoreProperty = ['jenisTraining'];
  const { data: dataBusinessUnit, isPending: loadingBusinessUnit } = useQuery({
    queryKey: ['getBusinessUnitCreateLCMS'],
    queryFn: () => courseService.getBusinessUnitCreateLCMS()
  });

  // eslint-disable-next-line no-unused-vars
  const { tags, startDateTime, endDateTime, ...others } = basicInformation || {};
  return (
    <Card>
      <Stack gap={16}>
        <Group justify='space-between'>
          <Group gap={8}>
            <HiOutlineBookmark />
            <Text c={'dark'} fw={700}>
              Basic Information
            </Text>
          </Group>
          <Button
            variant='light'
            rightSection={<HiExternalLink />}
            onClick={() =>
              window.open(
                `${process.env.REACT_APP_LPD_URL || 'http://dev-talent-comp-dev.apps.binus.edu'}/course/${beelajarID}`
              )
            }
          >
            LPD{' '}
          </Button>
        </Group>

        <Stack gap={0}>
          <Text c={'dark'} fw={600} fz={14}>
            Beelajar ID
          </Text>
          <Box w={'fit-content'}>
            <MaterialLink
              title={beelajarID}
              linkMaterial={
                beelajarID
                  ? `${process.env.REACT_APP_BEELAJAR_URL?.replace(
                      '/webservice/rest/server.php',
                      ''
                    )}/course/view.php?id=${beelajarID}`
                  : ''
              }
              isBeelajarLoginLink
            />
          </Box>
        </Stack>

        <Stack gap={0}>
          <Text c={'dark'} fw={600} fz={14}>
            BCL&D Module Code
          </Text>
          <Text fz={14} c={'secondary'}>
            {bcldModuleCode || '-'}
          </Text>
        </Stack>

        {Object.keys(others).map((key, index) => {
          if (key === 'businessUnits') {
            if ((others[key as keyof typeof others] as string[]).length === 0) {
              return (
                <Stack gap={0}>
                  <Text c={'dark'} fw={600} fz={14}>
                    {labelMapping[key] || key}
                  </Text>
                  <Text fz={14} c={'secondary'}>
                    All
                  </Text>
                </Stack>
              );
            }
            return (
              <Stack key={index} gap={0}>
                <Text c={'dark'} fw={600} fz={14}>
                  {labelMapping[key] || key}
                </Text>
                {!loadingBusinessUnit &&
                  (others[key as keyof typeof others] as string[])?.map(
                    (unit: string, index: number) => {
                      const businessUnit = dataBusinessUnit?.data?.find(
                        (item) => item.buCode === unit
                      );
                      return (
                        <Text key={index} fz={14} c={'secondary'}>
                          {businessUnit?.buName || unit}
                        </Text>
                      );
                    }
                  )}
              </Stack>
            );
          }
          if (key === 'learningContent') {
            return (
              <Stack key={index} gap={0}>
                <Text c={'dark'} fw={600} fz={14}>
                  {labelMapping[key] || key}
                </Text>
                <Paper withBorder p={24} style={{ overflow: 'auto' }}>
                  <div dangerouslySetInnerHTML={{ __html: others[key as keyof typeof others] }} />
                </Paper>
              </Stack>
            );
          }

          if (key === 'description') {
            return (
              <Stack key={index} gap={0}>
                <Text c={'dark'} fw={600} fz={14}>
                  {labelMapping[key] || key}
                </Text>
                <div
                  dangerouslySetInnerHTML={{
                    __html: others[key as keyof typeof others]
                  }}
                  style={{ whiteSpace: 'pre-wrap' }}
                />
              </Stack>
            );
          }

          if (ignoreProperty.includes(key)) {
            return null;
          }

          return (
            <Stack key={index} gap={0}>
              <Text c={'dark'} fw={600} fz={14}>
                {labelMapping[key] || key}
              </Text>
              <Text fz={14} c={'secondary'}>
                {others[key as keyof typeof others] || '-'}
              </Text>
            </Stack>
          );
        })}

        <Stack gap={0}>
          <Text c={'dark'} fw={600} fz={14}>
            {labelMapping['learningObjectives']}
          </Text>
          {learningObjectives ? (
            <Paper withBorder px={24} style={{ overflow: 'auto' }}>
              <div dangerouslySetInnerHTML={{ __html: learningObjectives }} />
            </Paper>
          ) : (
            <Text fz={14} c={'secondary'}>
              -
            </Text>
          )}
        </Stack>
        <Stack gap={0}>
          <Text c={'dark'} fw={600} fz={14}>
            Tags
          </Text>
          <Group gap={4}>
            {tags &&
              tags.map((tag, index) => (
                <Badge key={index} styleVariant='default'>
                  {tag}
                </Badge>
              ))}

            {tags?.length === 0 && '-'}
          </Group>
        </Stack>
      </Stack>
    </Card>
  );
};

export default BasicInformationSection;
